// Master Apps · popover de informação reutilizável, à prova de corte.
//
// Substitui o padrão antigo (painel com `position: absolute` dentro de um
// container de scroll), que era engolido pelo overflow quando o botão ficava
// perto do fim do container. Aqui o painel vai para o `document.body` via
// portal com `position: fixed`, e a posição é calculada a partir do
// `getBoundingClientRect()` do botão: abre abaixo por padrão, sobe quando não
// couber, e faz clamp horizontal para nunca sair da viewport.
//
// Linguagem visual portada do InfoButton de register-screen.tsx
// (cabeçalho com título + subtítulo, seções h/b/list, seta, radius 12).
//
// No compacto (≤1024px, via `useViewport()`) a mesma geometria já entrega o
// painel com 100vw − 24px; o que muda é a ergonomia: ícone de 26px em vez de 18,
// "fechar" de 40px, 78vh de teto (menos rolagem) e texto um ponto maior. O
// desktop renderiza igual ao de antes.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconInfo, IconX } from '@/components/icons';
import { byViewport, useViewport } from '@/hooks/use-viewport';
import { fontSans, makePalette, rotuloCaixaAlta } from '@/lib/theme';

// ─── Tipos ────────────────────────────────────
export interface InfoSection {
  /** Cabeçalho da seção (renderizado em caixa alta, monoespaçado). */
  h: string;
  /** Parágrafo corrido. */
  b?: string;
  /** Itens com marcador. */
  list?: string[];
}

export interface InfoPopoverProps {
  title: string;
  subtitle?: string;
  sections: InfoSection[];
  dark: boolean;
  accent: string;
  /** Diâmetro do botão "i" · aceita 18 a 22 (padrão 22). */
  size?: number;
}

export interface InfoPopoverContentProps {
  sections: InfoSection[];
  dark: boolean;
  accent: string;
}

interface PanelPos {
  top: number;
  left: number;
  width: number;
  above: boolean;
  /** Posição horizontal da seta, relativa à borda esquerda do painel. */
  arrow: number;
  arrowVisible: boolean;
}

// ─── Constantes de layout ─────────────────────
const PANEL_W = 420; // largura alvo (400–440)
const GAP = 10; // respiro entre botão e painel
const EDGE = 12; // margem mínima da viewport
const EST_H = 380; // altura presumida antes da primeira medição

const KEYFRAMES = `@keyframes mapp-info-pop-down{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}
@keyframes mapp-info-pop-up{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}`;

/** Cor do ícone "i": aberto vence hover, e hover vence o estado neutro. */
function glyphColor(open: boolean, hover: boolean, accent: string, muted: string): string {
  if (open) return '#fff';
  if (hover) return accent;
  return muted;
}

/**
 * Geometria do painel a partir do retângulo do botão e da viewport. Pura — toda
 * a decisão de posicionamento (abrir para baixo/cima, clamp nas bordas, seta)
 * vive aqui, fora do componente.
 */
function computePanelPos(r: DOMRect, vw: number, vh: number, height: number): PanelPos {
  // O `min` com a viewport JÁ é a regra do celular: em 320px a largura alvo de
  // 420px cai para 296 (= 100vw − 2×12). Não há ramo por faixa aqui de
  // propósito — o painel é tão largo quanto a tela permite em qualquer largura.
  const width = Math.min(PANEL_W, vw - 2 * EDGE);

  // Vertical · abaixo por padrão; acima quando não couber e houver mais
  // espaço em cima.
  const spaceBelow = vh - r.bottom;
  const above = spaceBelow < height + 16 && r.top - 16 > spaceBelow;
  const rawTop = above ? r.top - GAP - height : r.bottom + GAP;
  const maxTop = Math.max(EDGE, vh - height - EDGE);
  const top = Math.min(Math.max(EDGE, rawTop), maxTop);

  // Horizontal · alinhado à direita do botão (como no InfoButton original),
  // com clamp para nunca vazar da viewport.
  const maxLeft = Math.max(EDGE, vw - width - EDGE);
  const left = Math.min(Math.max(EDGE, r.right + 8 - width), maxLeft);

  // Seta · segue o centro do botão e desaparece se sair do painel ou se o
  // clamp vertical desencostou o painel do botão.
  const arrow = r.left + r.width / 2 - left;
  const adjacent = Math.abs(top - rawTop) < 0.5;
  const arrowVisible = adjacent && arrow >= 16 && arrow <= width - 16;

  return { top, left, width, above, arrow, arrowVisible };
}

/**
 * Liga os listeners de janela enquanto o painel está aberto e devolve o
 * desligamento. `capture` no scroll é obrigatório para pegar o scroll de
 * containers internos, que não borbulha.
 */
function bindWindowListeners(measure: () => void, close: () => void): () => void {
  const onReflow = () => measure();
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
  };
  window.addEventListener('resize', onReflow);
  window.addEventListener('scroll', onReflow, true);
  window.addEventListener('keydown', onKey);
  return () => {
    window.removeEventListener('resize', onReflow);
    window.removeEventListener('scroll', onReflow, true);
    window.removeEventListener('keydown', onKey);
  };
}

function samePos(a: PanelPos | null, b: PanelPos): boolean {
  return (
    a !== null &&
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    a.width === b.width &&
    a.above === b.above &&
    Math.abs(a.arrow - b.arrow) < 0.5 &&
    a.arrowVisible === b.arrowVisible
  );
}

// ─── Conteúdo formatado das seções ────────────
// Exportado à parte para reaproveitar o mesmo formato h/b/list fora do popover
// (ex.: em um drawer de ajuda ou numa página de guia).
export function InfoPopoverContent({ sections, dark, accent }: Readonly<InfoPopoverContentProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  // Corpo um ponto maior no compacto: 12.5px de texto explicativo num painel de
  // 296px vira parágrafo de jornal, e o `letterSpacing` largo do cabeçalho de
  // seção some em 9px na tela do celular.
  const body = byViewport(vp, { mobile: 13.5, desktop: 12.5 });
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: byViewport(vp, { mobile: 14, desktop: 16 }),
      }}
    >
      {sections.map((s) => (
        <div key={s.h}>
          <div
            style={{
              ...rotuloCaixaAlta,
              fontSize: byViewport(vp, { mobile: 10, desktop: 9 }),
              fontWeight: 700,
              color: p.muted,
              textTransform: 'uppercase',
              letterSpacing: byViewport(vp, { mobile: '0.1em', desktop: '0.14em' }),
              marginBottom: 7,
            }}
          >
            {s.h}
          </div>
          {s.b && <div style={{ fontSize: body, color: p.textMuted, lineHeight: 1.6 }}>{s.b}</div>}
          {s.list && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                marginTop: s.b ? 8 : 0,
              }}
            >
              {s.list.map((item) => (
                <div
                  key={item}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    fontSize: body,
                    color: p.textMuted,
                    lineHeight: 1.5,
                  }}
                >
                  <span
                    style={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      background: accent,
                      marginTop: 7,
                      flexShrink: 0,
                    }}
                  />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Popover ──────────────────────────────────
export function InfoPopover({
  title,
  subtitle,
  sections,
  dark,
  accent,
  size = 22,
}: Readonly<InfoPopoverProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [pos, setPos] = useState<PanelPos | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDialogElement | null>(null);

  // Piso do diâmetro por faixa: no toque o ícone de 18px que as telas pedem sobe
  // para 26px (+44% de alvo). NÃO vai aos 40px de recomendação de toque por dois
  // motivos medidos: (1) o botão é inline no rótulo do campo ("Criticidade * i"),
  // e um círculo de 40px viraria o elemento mais pesado do formulário, com 12
  // rótulos assim na mesma tela; (2) as duas alternativas para ter 40px de alvo
  // sem ocupar 40px de layout (filho absoluto com `inset: -8` ou margem negativa)
  // estendem o `scrollWidth` do ancestral em 8px — apareceu na sonda como
  // rolagem horizontal fantasma no <span> de embrulho (26px de cliente contra
  // 34px de rolagem). Alvo honesto e sem efeito colateral vence alvo maior com
  // rolagem inventada.
  const btnSize = Math.max(byViewport(vp, { mobile: 26, desktop: 18 }), Math.min(22, size));

  const close = useCallback(() => {
    setOpen(false);
    setPos(null);
  }, []);

  // Calcula a posição do painel a partir do retângulo do botão.
  const measure = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const height = panelRef.current?.offsetHeight || EST_H;
    const next = computePanelPos(
      btn.getBoundingClientRect(),
      window.innerWidth,
      window.innerHeight,
      height,
    );
    setPos((prev) => (samePos(prev, next) ? prev : next));
  }, []);

  // Primeira medição antes da pintura (o painel já está no DOM, oculto).
  useLayoutEffect(() => {
    if (!open) return;
    measure();
  }, [open, measure]);

  // Reposiciona em resize/scroll (capture pega scroll de containers internos),
  // fecha no Escape.
  useEffect(() => {
    if (!open) return;
    return bindWindowListeners(measure, close);
  }, [open, measure, close]);

  const placed = pos !== null;
  // Geometria em uso: a medida quando existe, senão o palpite do paint anterior à
  // primeira medição (painel no canto, invisível e sem eventos, com a seta
  // escondida). Um único lugar decide o fallback, em vez de um `placed ? … : …`
  // por declaração do style.
  const geo: PanelPos = pos ?? {
    top: 0,
    left: 0,
    width: Math.min(PANEL_W, window.innerWidth - 2 * EDGE),
    above: false,
    arrow: 0,
    arrowVisible: false,
  };
  const arrowBg = geo.above ? p.surface : `linear-gradient(${accent}08, ${accent}08), ${p.surface}`;
  const animationName = geo.above ? 'mapp-info-pop-up' : 'mapp-info-pop-down';

  return (
    // O `stopPropagation` do clique é obrigatório porque o painel vai por
    // PORTAL: no React o evento sobe pela árvore de COMPONENTES, não pela do
    // DOM, então sem isso um clique dentro do painel (ou no fundo invisível)
    // chegaria ao card clicável que costuma envolver o botão "i".
    // `role="presentation"` declara o que este span é: embrulho de layout e
    // barreira de evento, não um controle — quem é clicável aqui é o <button>
    // filho, que já responde a teclado por conta própria. Sem isso o span
    // pareceria um controle sem suporte a teclado.
    <span
      role="presentation"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      style={{ display: 'inline-flex', flexShrink: 0 }}
    >
      <button
        ref={btnRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          if (open) close();
          else setOpen(true);
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        title="Mais informações"
        style={{
          width: btnSize,
          height: btnSize,
          borderRadius: '50%',
          // O círculo é DESENHADO pelo próprio ícone SVG (traço consistente com
          // o resto da iconografia) — o botão não tem borda própria. Era uma
          // letra "i" itálica dentro de um anel de CSS, que ficava torta e
          // fora do padrão dos demais ícones.
          background: open ? accent : 'transparent',
          color: glyphColor(open, hover, accent, p.muted),
          border: 'none',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'color 120ms, background 120ms',
          padding: 0,
          flexShrink: 0,
        }}
      >
        <IconInfo size={btnSize} stroke={1.9} />
      </button>

      {open &&
        createPortal(
          <>
            <style>{KEYFRAMES}</style>

            {/* Fundo invisível para fechar ao clicar fora. É decorativo (não tem
                conteúdo nem rótulo) e `aria-hidden` o mantém fora da árvore de
                acessibilidade — mesma escolha do painel do MultiSelect. Quem
                navega por teclado fecha com Escape, tratado no listener de
                window acima; um backdrop focável só criaria uma parada de
                tabulação invisível. */}
            <div
              aria-hidden="true"
              onClick={close}
              style={{ position: 'fixed', inset: 0, zIndex: 9000 }}
            />

            {/* `<dialog open>` (não-modal) em vez de `div role="dialog"`: mesma
                renderização, semântica nativa. As quatro primeiras declarações
                do style neutralizam o stylesheet do agente — sem elas o UA
                aplicaria `margin: auto` (com `inset-inline-end: 0` isso
                deslocaria o painel), `padding: 1em`, `overflow: auto` (que
                recortaria a seta, posicionada fora das bordas) e
                `color: CanvasText` (que quebraria a herança de cor). */}
            <dialog
              ref={panelRef}
              open
              aria-label={title}
              style={{
                margin: 0,
                padding: 0,
                overflow: 'visible',
                color: 'inherit',
                position: 'fixed',
                top: geo.top,
                left: geo.left,
                width: geo.width,
                maxWidth: 'calc(100vw - 24px)',
                // Fatia maior da tela no compacto: com 70vh o mesmo texto que
                // cabia em 420px de largura precisava de duas rolagens em 296px.
                // Nos dois casos sobra folga para o clamp de `computePanelPos`
                // (78vh + 2×12px de margem < 100vh para qualquer altura de tela).
                maxHeight: byViewport(vp, {
                  mobile: 'min(78vh, 560px)',
                  desktop: 'min(70vh, 520px)',
                }),
                visibility: placed ? 'visible' : 'hidden',
                pointerEvents: placed ? 'auto' : 'none',
                zIndex: 9001,
                display: 'flex',
                flexDirection: 'column',
                background: p.surface,
                border: `1px solid ${p.border}`,
                borderRadius: 12,
                boxShadow: '0 16px 48px -12px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.04)',
                fontFamily: fontSans,
                animation: placed ? `${animationName} 140ms ease-out` : undefined,
              }}
            >
              {/* Seta apontando para o botão */}
              {geo.arrowVisible && (
                <div
                  style={{
                    position: 'absolute',
                    top: geo.above ? undefined : -7,
                    bottom: geo.above ? -7 : undefined,
                    left: geo.arrow - 6,
                    width: 12,
                    height: 12,
                    background: arrowBg,
                    borderLeft: geo.above ? 'none' : `1px solid ${p.border}`,
                    borderTop: geo.above ? 'none' : `1px solid ${p.border}`,
                    borderRight: geo.above ? `1px solid ${p.border}` : 'none',
                    borderBottom: geo.above ? `1px solid ${p.border}` : 'none',
                    transform: 'rotate(45deg)',
                  }}
                />
              )}

              {/* Cabeçalho fixo */}
              <div
                style={{
                  padding: byViewport(vp, { mobile: '12px 12px 11px', desktop: '16px 18px 14px' }),
                  borderBottom: `1px solid ${p.border}`,
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                  background: dark ? `${accent}08` : `${accent}06`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: byViewport(vp, { mobile: 10, desktop: 12 }),
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: byViewport(vp, { mobile: 28, desktop: 32 }),
                    height: byViewport(vp, { mobile: 28, desktop: 32 }),
                    borderRadius: 8,
                    background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)`,
                    color: '#fff',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconInfo size={byViewport(vp, { mobile: 15, desktop: 17 })} stroke={2} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {subtitle && (
                    <div
                      style={{
                        ...rotuloCaixaAlta,
                        fontSize: 9,
                        fontWeight: 700,
                        color: accent,
                        textTransform: 'uppercase',
                        letterSpacing: '0.16em',
                        marginBottom: 3,
                      }}
                    >
                      {subtitle}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: p.text,
                      letterSpacing: '-0.012em',
                    }}
                  >
                    {title}
                  </div>
                </div>
                <button
                  type="button"
                  aria-label="Fechar"
                  onClick={close}
                  style={{
                    // 40px no toque (24px era metade da polpa do dedo). Aqui cabe
                    // o alvo cheio: o cabeçalho tem largura sobrando e o botão é
                    // transparente, então o "x" continua visualmente do mesmo
                    // tamanho — cresce só a área.
                    width: byViewport(vp, { mobile: 40, desktop: 24 }),
                    height: byViewport(vp, { mobile: 40, desktop: 24 }),
                    // Compensa o alvo maior para o "x" não descolar do canto.
                    margin: byViewport(vp, { mobile: '-8px -8px 0 0', desktop: '0' }),
                    borderRadius: 6,
                    background: 'transparent',
                    border: 'none',
                    color: p.muted,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconX size={byViewport(vp, { mobile: 15, desktop: 13 })} />
                </button>
              </div>

              {/* Corpo rolável */}
              <div
                style={{
                  padding: byViewport(vp, { mobile: 14, desktop: 18 }),
                  overflowY: 'auto',
                  minHeight: 0,
                  flex: 1,
                }}
              >
                <InfoPopoverContent sections={sections} dark={dark} accent={accent} />
              </div>
            </dialog>
          </>,
          document.body,
        )}
    </span>
  );
}
